import {
  FormulaFields,
  FormulaRecord,
  PromptType,
} from 'services/store/ai/types';
import { FilePickerMode } from 'pages/AIMusicGenerator/components/Sidebar/components/UserLoopModal/components/LoopFileSelector/constants';
import { AnalyticsEventLocation } from '../entities';
import AnalyticsEvent from './analyticsEvent';

const getAudioClipType = (fileName: string) =>
  /^recording-/.test(fileName) ? FilePickerMode.RECORD : FilePickerMode.UPLOAD;

const aiMusicGeneratorEvents = {
  infoGuideClick: (): AnalyticsEvent =>
    AnalyticsEvent.createEvent('info_guide_clicked'),
  shareFormulaClick: (genre: string, formula: FormulaRecord): AnalyticsEvent =>
    AnalyticsEvent.createEvent('ai_share_formula_click', {
      genre,
      formula,
    }),
  copyFormulaUrl: (genre: string, formula: FormulaRecord): AnalyticsEvent =>
    AnalyticsEvent.createEvent('ai_copy_formula_url', {
      genre,
      formula,
    }),
  copySongClick: (): AnalyticsEvent =>
    AnalyticsEvent.createEvent('copy_song_link_clicked'),

  aiGenerateButtonAnalytics: (data: {
    promptType: PromptType;
    primaryGenre?: string | null;
    subGenre?: string | null;
    formulaValues?: FormulaRecord;
    promptText?: string;
  }): AnalyticsEvent => {
    const { promptType, primaryGenre, subGenre, formulaValues, promptText } =
      data;

    const dataLayer: Record<string, unknown> = {
      prompt_type: promptType,
    };

    if (formulaValues?.user_loop) {
      dataLayer.hasUserLoop = true;
      dataLayer.entropy = formulaValues.user_loop.entropy;
      dataLayer.audioClipType = getAudioClipType(
        formulaValues.user_loop.loopFileName
      );
    }

    if (promptType === PromptType.TextToMusic) {
      dataLayer.chars_count = promptText?.length;
    }

    if (promptType === PromptType.AdvancedFormula) {
      dataLayer.genre = primaryGenre;
      dataLayer.subGenre = subGenre;
      const fields = Object.values(FormulaFields);
      const analyticsFormula = fields.reduce<FormulaRecord>(
        (acc, field) => ({
          ...acc,
          [field]: formulaValues?.[field],
        }),
        {}
      );
      dataLayer.formula = analyticsFormula;
    }

    return AnalyticsEvent.createEvent('ai_track_generate', dataLayer);
  },
  aiTrackRendered: (): AnalyticsEvent =>
    AnalyticsEvent.createEvent('ai_track_rendered'),
  aiTrackRegenerate: (): AnalyticsEvent =>
    AnalyticsEvent.createEvent('ai_track_regenerate'),
  playAIGeneratedSong: (
    title: string,
    location: AnalyticsEventLocation
  ): AnalyticsEvent =>
    AnalyticsEvent.createEvent('play_song', { title, location }),
  audioUploadClick: (location: string): AnalyticsEvent =>
    AnalyticsEvent.createEvent('audio_clip_upload_click', { location }),
  audioUploadSuccess: (location: string): AnalyticsEvent =>
    AnalyticsEvent.createEvent('audio_clip_uploaded', { location }),
  audioUploadError: (
    description: string,
    fileType: string,
    location: string
  ): AnalyticsEvent =>
    AnalyticsEvent.createEvent('upload_audio_clip_validation_description', {
      description,
      fileType,

      location,
    }),
  audioClipRecorded: ({ duration }: { duration: number }): AnalyticsEvent =>
    AnalyticsEvent.createEvent('audio_clip_recorded', {
      duration: Math.round(duration * 100) / 100,
    }),
  audioClipSelectionClick: ({
    clipType,
  }: {
    clipType: string;
  }): AnalyticsEvent =>
    AnalyticsEvent.createEvent('audio_clip_selection_click', {
      type: clipType,
    }),
};

export default aiMusicGeneratorEvents;
